/* eslint-disable no-param-reassign */
/* eslint-disable max-lines */
import dayjs from 'dayjs';
import getFieldValues from 'pages/rules/utils/getFieldValues';
import findCustomMatches from 'pages/rules/utils/findCustomMatches';
import loadConfig from 'components/queryBuilder/query-builder/sqlQueryBuilder/config';
import createFields from 'components/queryBuilder/query-builder/sqlQueryBuilder/custom_utils/createFields';
import applyJsonLogic from 'pages/rules/utils/applyJsonLogic';
import SQLQueryBuilderCustomUtils from 'components/queryBuilder/query-builder/sqlQueryBuilder/custom_utils/SQLQueryBuilderCustomUtils';
import { initialValuesQueryBuilder } from 'pages/rules/RulesDummyData';
import constants from 'core/constants/Constants';
import getFieldValuesForSimpleQuery from 'pages/rules/utils/getFieldValuesSimpleQuery';

export default function updatedValues(
  value,
  feelExpression,
  ruleExpression,
  configFields,
  inputSourceFileData
) {
  // ===== DATE HANDLING =====
  const getDaterange = value?.rules_date;
  let startDate = dayjs();
  let endDate = dayjs();
  if (getDaterange && Array.isArray(getDaterange) && getDaterange.length >= 2) {
    startDate = dayjs(getDaterange[0]);
    endDate = dayjs(getDaterange[1]);
  }

  // ===== CONDITION FLAGS =====
  const isOnlyNumber = Boolean(value?.enter_amount);
  const isGroupAdjustment = value?.adjustment_level === 'group';
  const isSimpleType = constants.payoutTypes
    .map((item) => item?.value)
    .includes(feelExpression?.type);
  const isSqlMode = value?.output_formula_mode_type === 'sql';
  const isSqlRuleApplicablityMode =
    value?.rule_applicability_mode_type === 'sql';

  // ===== HELPER FUNCTIONS =====

  /**
   * Get the formatted feel expression value (output formula)
   */
  function getFeelExpressionValue() {
    if (!feelExpression) return null;

    if (feelExpression?.caseList && isSqlMode) {
      const elseObjFeel = {
        type: feelExpression?.elseType,
        value: feelExpression?.elseValue,
        query: feelExpression?.elseQuery,
      };
      return SQLQueryBuilderCustomUtils.sqlFormatter(
        feelExpression,
        elseObjFeel
      );
    }
    return feelExpression?.value;
  }

  /**
   * Get the formatted rule expression value (rule applicability)
   */
  function getRuleExpressionValue() {
    // Always true for number-only or group adjustment
    if (isOnlyNumber || isGroupAdjustment) {
      return '*';
    }

    if (!ruleExpression) return null;

    if (ruleExpression?.caseList) {
      const elseObjRule = {
        type: ruleExpression?.elseType,
        value: ruleExpression?.elseValue,
        query: ruleExpression?.elseQuery,
      };
      return SQLQueryBuilderCustomUtils.sqlFormatter(
        ruleExpression,
        elseObjRule
      );
    }
    return ruleExpression?.value;
  }

  /**
   * Prepare expression for logic processing (remove query property)
   */
  function prepareExpressionForLogic(expression, defaultQuery = null) {
    if (!expression) return { query: defaultQuery };

    if (isSqlMode) {
      if (
        expression &&
        typeof expression === 'object' &&
        !Array.isArray(expression)
      ) {
        const { query, ...rest } = expression;
        console.log(query, 'Expression query');
        return rest;
      }
      return expression;
    }
    return { query: expression?.query || defaultQuery };
  }

  function prepareExpressionRuleForLogic(expression, defaultQuery = null) {
    if (!expression) return { query: defaultQuery };

    if (isSqlRuleApplicablityMode) {
      if (
        expression &&
        typeof expression === 'object' &&
        !Array.isArray(expression)
      ) {
        const { query, ...rest } = expression;
        console.log(query, 'Expression query');
        return rest;
      }
      return expression;
    }
    return { query: expression?.query || defaultQuery };
  }

  const feelExpressionValue = getFeelExpressionValue();
  const ruleExpressionValue = getRuleExpressionValue();

  // ===== EXPRESSION PREPARATION FOR LOGIC =====

  let feelExpressionForLogic;
  let ruleExpressionForLogic;

  if (isOnlyNumber || isGroupAdjustment) {
    // For number-only or group adjustment: simplify both expressions
    feelExpressionForLogic = isOnlyNumber
      ? { value: value.enter_amount }
      : prepareExpressionForLogic(feelExpression);
    ruleExpressionForLogic = { query: '*' };
  } else {
    feelExpressionForLogic = prepareExpressionForLogic(feelExpression);
    ruleExpressionForLogic = prepareExpressionRuleForLogic(ruleExpression);
  }

  // ===== OUTPUT FORMULA VALUE =====

  function getOutputFormulaValue(val) {
    // Priority 1: Only number entry
    if (isOnlyNumber) {
      return {
        query: '*',
        sql: val.enter_amount,
        value: val.enter_amount,
        type: 'NUMBER',
      };
    }

    // Priority 2: Simple type (payout types)
    if (isSimpleType) {
      return {
        query: feelExpression?.query || {},
        sql: feelExpressionValue || {},
        value: feelExpressionValue,
        type: feelExpression?.type || 'SIMPLE',
        fields: feelExpression?.fields || [],
      };
    }

    // Priority 3: SQL mode with case list
    if (isSqlMode && feelExpression?.caseList?.length > 0) {
      return feelExpression;
    }

    // Priority 4: Single mode or default with query
    if (
      (val?.output_formula_mode_type === 'single' ||
        !val?.output_formula_mode_type) &&
      feelExpression?.query
    ) {
      return {
        query: feelExpression.query,
        sql: feelExpressionValue,
        value: feelExpressionValue,
        type: 'QUERY',
      };
    }

    // Fallback
    return initialValuesQueryBuilder;
  }

  // ===== RULE APPLICABILITY VALUE =====

  function getRuleApplicabilityValue(val) {
    // Priority 1: Only number or group adjustment (always true)
    if (isOnlyNumber || isGroupAdjustment) {
      return {
        query: '*',
        sql: '*',
        value: '*',
        type: 'ALWAYS_TRUE',
      };
    }

    // Priority 2: SQL mode with case list
    if (isSqlRuleApplicablityMode && ruleExpression?.caseList?.length > 0) {
      return ruleExpression;
    }

    // Priority 3: Single mode or default with query
    if (
      (val?.rule_applicability_mode_type === 'single' ||
        !val?.rule_applicability_mode_type) &&
      ruleExpression?.query
    ) {
      return {
        query: ruleExpression.query,
        sql: ruleExpressionValue,
        value: ruleExpressionValue,
        type: 'QUERY',
      };
    }

    // Fallback
    return initialValuesQueryBuilder;
  }

  // ===== FIELD EXTRACTION AND MATCHING =====

  const fieldFeelExpression =
    isSimpleType && !isOnlyNumber
      ? getFieldValuesForSimpleQuery(feelExpression?.fields || [])
      : getFieldValues(feelExpressionForLogic);

  const customMatchesFeelExpression = findCustomMatches(
    fieldFeelExpression,
    configFields
  );

  const fieldsRuleExpression =
    isOnlyNumber || isGroupAdjustment
      ? []
      : getFieldValues(ruleExpressionForLogic);

  const customMatchesRuleExpression = findCustomMatches(
    fieldsRuleExpression,
    configFields
  );

  console.log(configFields, 'configFields for expression data');
  console.log(
    fieldFeelExpression,
    fieldsRuleExpression,
    'raw values for expressiondata'
  );
  console.log(
    customMatchesFeelExpression,
    customMatchesRuleExpression,
    'values for expressiondata'
  );

  // ===== JSON LOGIC GENERATION =====

  const loadedConfig = loadConfig('antd', createFields(configFields));

  /**
   * Generate feel logic based on conditions
   * CRITICAL: This ensures proper logic is always returned
   */
  function getFeelLogic() {
    // Case 1: Only number entry - simple addition logic
    if (isOnlyNumber) {
      return {
        and: [
          {
            '+': [0, Number(value?.enter_amount) || 0],
          },
        ],
      };
    }

    // Case 2: Simple type with existing jsonLogic
    if (isSimpleType && feelExpression?.jsonLogic?.logic) {
      return feelExpression.jsonLogic.logic;
    }

    // Case 3: Generate logic from expression
    try {
      const generatedLogic = applyJsonLogic(
        feelExpressionForLogic,
        loadedConfig
      );
      // Ensure we have valid logic
      return generatedLogic || { and: [] };
    } catch (error) {
      console.error('Error generating feel logic:', error);
      return { and: [] };
    }
  }

  /**
   * Generate rule logic based on conditions
   * CRITICAL: This ensures proper logic is always returned
   */
  function getRuleLogic() {
    // Case 1: Only number or group adjustment - always true
    if (isOnlyNumber || isGroupAdjustment) {
      return { '==': [1, 1] };
    }

    // Case 2: Generate logic from expression
    try {
      const generatedLogic = applyJsonLogic(
        ruleExpressionForLogic,
        loadedConfig
      );
      // Ensure we have valid logic
      return generatedLogic || { '==': [1, 1] };
    } catch (error) {
      console.error('Error generating rule logic:', error);
      return { '==': [1, 1] };
    }
  }

  const feellogic = getFeelLogic();
  const rulelogic = getRuleLogic();

  // ===== CONSTRUCT FINAL OBJECT =====

  const updatedValue = {
    rule_start_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
    rule_end_date: endDate.format('YYYY-MM-DD HH:mm:ss'),
    rule_file_path: inputSourceFileData?.file_path || null,
    rule_file_type: isOnlyNumber ? 'none' : 'dmn',
    rule_file_path_json: {
      file_path: inputSourceFileData?.file_path || null,
      file_upload_status:
        inputSourceFileData?.file_upload_status === true
          ? 'done'
          : inputSourceFileData?.file_upload_status || 'pending',
      file_uploaded_time: inputSourceFileData?.createdAt || null,
      file_name: inputSourceFileData?.file_name || null,
      file_id: Date.now(),
    },
    reporting_payout_variable_name:
      value?.merge_with_tag_name && value?.tags ? value.tags : value.rule_name,
    merge_with_tag_name: !!value?.merge_with_tag_name,
    tags: value?.merge_with_tag_name && value?.tags ? value.tags : '',
    output_formula_mode_type: value?.output_formula_mode_type || 'single',
    rule_applicability_mode_type:
      value?.rule_applicability_mode_type || 'single',
    output_formula: {
      ...getOutputFormulaValue(value),
      jsonLogic: {
        logic: feellogic,
        data: isOnlyNumber
          ? {}
          : customMatchesFeelExpression.reduce(
              (acc, key) => ({ ...acc, [key]: null }),
              {}
            ),
      },
    },

    rule_applicability: {
      ...getRuleApplicabilityValue(value),
      jsonLogic: {
        ...(ruleExpression?.jsonLogic || {}),
        logic: rulelogic,
        data:
          isOnlyNumber || isGroupAdjustment
            ? {}
            : customMatchesRuleExpression.reduce(
                (acc, key) => ({ ...acc, [key]: null }),
                {}
              ),
      },
    },

    output_formula_json: {
      value: feelExpressionValue,
      jsonLogic: {
        logic: feellogic,
        data: isOnlyNumber ? {} : customMatchesFeelExpression,
      },
    },

    rule_applicability_json: {
      value: ruleExpressionValue,
      jsonLogic: {
        logic: rulelogic,
        data:
          isOnlyNumber || isGroupAdjustment ? {} : customMatchesRuleExpression,
      },
    },
  };

  return updatedValue;
}
